import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import PropertiesPage from "@/pages/PropertiesPage";
import DealCardPage from "@/pages/DealCardPage";
import DealTimelinePage from "@/pages/DealTimelinePage";
import MyDealsPage from "@/pages/MyDealsPage";
import ViewingsPage from "@/pages/ViewingsPage";
import ProtectedRoute from "@/components/ProtectedRoute";
import BuyerProfileIntake from "@/components/BuyerProfileIntake";

// Buyer Pages
import BuyerDashboardPage from "@/pages/BuyerDashboardPage";

// Seller Pages
import SellerDashboardPage from "@/pages/SellerDashboardPage";
import SellerOnboardingPage from "@/pages/SellerOnboardingPage";
import PropertyCreatePage from "@/pages/PropertyCreatePage";

// Agent Pages
import AgentDashboardPage from "@/pages/AgentDashboardPage";
import AgentApplicationPage from "@/pages/AgentApplicationPage";

// Admin Pages
import AdminDashboardPage from "@/pages/AdminDashboardPage";

// Investor Page
import InvestorPage from "@/pages/InvestorPage";
import InvestorDeckPDF from "@/pages/InvestorDeckPDF";
import InvestorDemo from "@/pages/InvestorDemo";
import EcosystemInvestorPage from "@/pages/EcosystemInvestorPage";

// Auth wrapper for protected routes
const AuthenticatedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse">
          <div className="w-16 h-16 rounded bg-primary/20 flex items-center justify-center">
            <span className="font-display text-2xl text-primary">R</span>
          </div>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public route wrapper (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse">
          <div className="w-16 h-16 rounded bg-primary/20" />
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/investor" element={<InvestorPage />} />
      <Route path="/investor/deck" element={<InvestorDeckPDF />} />
      <Route path="/investor/demo" element={<InvestorDemo />} />
      <Route path="/ecosystem" element={<EcosystemInvestorPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      
      {/* Property Routes */}
      <Route path="/properties" element={<PropertiesPage />} />
      <Route path="/property/:id" element={<PropertyDetailPage />} />
      <Route path="/deal-card/:propertyId" element={<DealCardPage />} />
      
      {/* Protected Routes */}
      <Route path="/onboarding" element={
        <AuthenticatedRoute>
          <OnboardingPage />
        </AuthenticatedRoute>
      } />
      <Route path="/buyer/profile" element={
        <AuthenticatedRoute>
          <BuyerProfileIntake onComplete={() => window.location.href = '/dashboard'} />
        </AuthenticatedRoute>
      } />
      <Route path="/dashboard" element={
        <AuthenticatedRoute>
          <DashboardPage />
        </AuthenticatedRoute>
      } />
      
      {/* Seller Routes */}
      <Route path="/seller/dashboard" element={
        <AuthenticatedRoute>
          <SellerDashboardPage />
        </AuthenticatedRoute>
      } />
      <Route path="/seller/onboarding" element={
        <AuthenticatedRoute>
          <SellerOnboardingPage />
        </AuthenticatedRoute>
      } />
      <Route path="/seller/property/create" element={
        <AuthenticatedRoute>
          <PropertyCreatePage />
        </AuthenticatedRoute>
      } />
      
      {/* Buyer Routes */}
      <Route path="/buyer/dashboard" element={
        <AuthenticatedRoute>
          <BuyerDashboardPage />
        </AuthenticatedRoute>
      } />
      
      {/* Agent Routes */}
      <Route path="/agent/dashboard" element={
        <AuthenticatedRoute>
          <AgentDashboardPage />
        </AuthenticatedRoute>
      } />
      <Route path="/agent/apply" element={
        <AuthenticatedRoute>
          <AgentApplicationPage />
        </AuthenticatedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <AuthenticatedRoute>
          <AdminDashboardPage />
        </AuthenticatedRoute>
      } />
      
      {/* Deal Timeline Route */}
      <Route path="/deals/:dealId" element={
        <AuthenticatedRoute>
          <DealTimelinePage />
        </AuthenticatedRoute>
      } />
      
      {/* My Deals List Route */}
      <Route path="/deals" element={
        <AuthenticatedRoute>
          <MyDealsPage />
        </AuthenticatedRoute>
      } />
      
      {/* Viewings Route */}
      <Route path="/viewings" element={
        <AuthenticatedRoute>
          <ViewingsPage />
        </AuthenticatedRoute>
      } />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
